import { BaseHTMLElement, customElement, first, onEvent } from 'dom-native';

import { DEFAULT_QUICK_PROMPTS } from '@/constants/quick-prompts';
import type { ChatMessage } from '@/types/chat';
import { icon } from '@/utils/icons';

import './help-agent-message-item';
import { HelpAgentMessageItem } from './help-agent-message-item';

function escapeAttribute(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function escapeHtml(value: string): string {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}

function toDataTestKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

@customElement('help-agent-message-list')
export class HelpAgentMessageList extends BaseHTMLElement {
  private _messages: ChatMessage[] = [];
  private _quickPrompts: readonly string[] = DEFAULT_QUICK_PROMPTS;

  init(): void {
    super.init();
    this.innerHTML = `
      <div class="help-agent-message-list-frame">
        <div class="help-agent-message-list-scroll" data-role="scroll" data-test="help-widget-message-list"></div>
      </div>
    `;
    this.renderMessages();
  }

  set messages(nextMessages: ChatMessage[]) {
    this._messages = [...nextMessages];
    if (this.initialized) {
      this.renderMessages();
    }
  }

  get messages(): ChatMessage[] {
    return this._messages;
  }

  set quickPrompts(nextQuickPrompts: readonly string[] | null | undefined) {
    this._quickPrompts = nextQuickPrompts && nextQuickPrompts.length > 0
      ? [...nextQuickPrompts]
      : DEFAULT_QUICK_PROMPTS;

    if (this.initialized) {
      this.renderMessages();
    }
  }

  get quickPrompts(): readonly string[] {
    return this._quickPrompts;
  }

  private renderMessages(): void {
    const scrollContainer = first(this, '[data-role="scroll"]') as HTMLElement | null;
    if (!scrollContainer) {
      return;
    }

    if (this._messages.length === 0) {
      scrollContainer.classList.add('is-empty');
      scrollContainer.innerHTML = `
        <section class="help-agent-empty-state" data-test="help-widget-empty-state">
          <div class="help-agent-prompts-header" data-test="help-widget-quick-prompts-header">
            <span class="help-agent-prompts-header-icon">${icon('info-circle', { size: 'var(--ha-content-heading-icon-size)' })}</span>
            <h2 class="help-agent-prompts-title">Quick prompts</h2>
          </div>
          <div class="help-agent-prompts-list" data-test="help-widget-quick-prompts">
            ${this._quickPrompts.map((prompt) => `
              <button
                type="button"
                class="help-agent-prompt-card"
                data-prompt="${escapeAttribute(prompt)}"
                data-test="help-widget-quick-prompt"
                data-test-key="${escapeAttribute(toDataTestKey(prompt))}"
                aria-label="${escapeAttribute(prompt)}"
              >
                <span class="help-agent-prompt-card-text">${escapeHtml(prompt)}</span>
              </button>
            `).join('')}
          </div>
        </section>
      `;
      scrollContainer.scrollTop = 0;
      return;
    }

    scrollContainer.classList.remove('is-empty');
    const fragment = document.createDocumentFragment();
    for (const message of this._messages) {
      const item = document.createElement('help-agent-message-item') as HelpAgentMessageItem;
      item.messageData = message;
      fragment.appendChild(item);
    }

    scrollContainer.replaceChildren(fragment);
    requestAnimationFrame(() => {
      scrollContainer.scrollTop = scrollContainer.scrollHeight;
    });
  }

  @onEvent('click', '[data-prompt]')
  onPromptClick(event: Event & { selectTarget: HTMLElement }): void {
    const prompt = event.selectTarget.getAttribute('data-prompt');
    if (!prompt) {
      return;
    }

    this.dispatchEvent(new CustomEvent('help-agent:select-prompt', {
      bubbles: true,
      composed: true,
      detail: { text: prompt },
    }));
  }
}
